<template>
  <dd-modal
    v-model:visible="internalShowModal"
    class="dashboard-archived"
    title="Archived Mailboxes and Projects"
    subtitle="Restore archived mailboxes and projects"
  >
    <loading-placeholder v-if="isLoading" :lines="20" />
    <template v-else-if="hasNoArchive">
      <p>All mailboxes and projects are active.</p>
    </template>
    <template v-else>
      <h5>Archived Projects</h5>
      <template v-if="hasNoArchivedProjects">
        <p>All projects are active.</p>
      </template>
      <template v-else>
        <div v-for="project in archivedProjects" :key="project.id" class="dashboard-archived__row">
          <span class="title"><i class="ddi ddi-project" />{{project.name}}</span>
          <dd-button
            :is-loading="project.id===currentlyRestoringID"
            size="small"
            type="flat"
            @click="restoreProject(project.id)"
          ><i class="ddi ddi-return"/>&nbsp;Restore</dd-button>
        </div>
      </template>
      <div class="dd-divider" />
      <h5>Archived Mailboxes</h5>
      <template v-if="hasNoArchivedMailboxes">
        <p>All mailboxes are active.</p>
      </template>
      <template v-else>
        <div v-for="mailbox in archivedMailboxes" :key="mailbox.id" class="dashboard-archived__row">
          <span class="title"><i class="ddi ddi-mailbox" />{{mailbox.name}}</span>
          <dd-button
            :is-loading="mailbox.id===currentlyRestoringID"
            size="small"
            type="flat"
            @click="restoreMailbox(mailbox.id)"
          >
            <i class="ddi ddi-return"/>&nbsp;Restore</dd-button>
        </div>
      </template>
    </template>
    <template v-if="showFooter" #footer>
      <span>
        <dd-button type="flat" @click="internalShowModal=false">Close window</dd-button>
      </span>
    </template>
  </dd-modal>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */
import { IItemForSelection } from '@/api/types/response.types'

/* API */
import DashboardAPI from '@/api/modules/dashboard.api'
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default defineComponent({
  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['mailbox-restored', 'project-restored', 'update:showModal'],

  data() {
    return {
      isLoading: true,
      currentlyRestoringID: 0,
      archivedProjects: [] as IItemForSelection[],
      archivedMailboxes: [] as IItemForSelection[],
    }
  },

  computed: {
    internalShowModal: {
      get(): boolean {
        return this.showModal
      },
      set(newValue: boolean) {
        this.$emit('update:showModal', newValue)
      },
    },
    hasNoArchive(): boolean {
      return this.hasNoArchivedMailboxes && this.hasNoArchivedProjects
    },
    hasNoArchivedProjects(): boolean {
      return this.archivedProjects.length === 0
    },
    hasNoArchivedMailboxes(): boolean {
      return this.archivedMailboxes.length === 0
    },
    showFooter(): boolean {
      return !this.isLoading && !this.hasNoArchive
    },
  },

  watch: {
    internalShowModal: {
      async handler(value) {
        if (value) {
          this.isLoading = true
          await this.fetchArchived()
          this.isLoading = false
        }
      },
    },
  },

  methods: {
    async fetchArchived() {
      const response = await DashboardAPI.get_Archived()
      this.archivedProjects = response.listInternalProjects
      this.archivedMailboxes = response.listMailboxes
    },
    async restoreProject(id: number) {
      try {
        this.currentlyRestoringID = id
        const response = await InternalProjectsAPI.post_Restore(id)
        this.archivedProjects = this.archivedProjects.filter(p => p.id !== id)
        this.$messaging.showSuccess(response.message)
        this.$emit('project-restored')
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.currentlyRestoringID = 0
      }
    },
    async restoreMailbox(id: number) {
      try {
        this.currentlyRestoringID = id
        const response = await MailboxesAPI.post_Restore(id)
        this.archivedMailboxes = this.archivedMailboxes.filter(m => m.id !== id)
        this.$messaging.showSuccess(response.message)
        this.$emit('mailbox-restored')
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.currentlyRestoringID = 0
      }
    },
  },
})
</script>

<style lang="scss">
.dashboard-archived {
  margin-bottom: 18px;

  p {
    padding: 20px 0;
    text-align: center;
  }

  &__row {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 10px 0;
    border-top: $border--solid-extra-light;

    > .title {
      margin-right: auto;
      font-weight: $bold;
      > i {
        font-size: 15px;
        padding: 0 8px 0 1px;
        color: var(--color-text-third);
      }
    }
  }

  .dd-divider {
    margin: 20px 0;
  }
}
</style>
